// The Organisations screens' server state (spec/14 §3.1, F-HQ-02), as TanStack Query hooks over the
// generated client — `packages/contracts/src/routes/hq-organizations.ts`:
//
//   GET    /v1/hq/organizations                                  every organisation and its owner's status, newest first
//   POST   /v1/hq/organizations                                  invite a café: create it, invite its owner
//   GET    /v1/hq/organizations/{organizationId}                 one
//   GET    /v1/hq/organizations/{o}/owner                        whether its owner joined, and its newest owner invitation
//   POST   /v1/hq/organizations/{o}/owner-invitations            invite an owner while it has none
//   POST   /v1/hq/organizations/{o}/owner-invitations/{i}/resend a fresh invitation to the same contact
//   POST   /v1/hq/organizations/{o}/owner-invitations/{i}/link   a pending invitation's link, to pass on
//   DELETE /v1/hq/organizations/{o}/owner-invitations/{i}        withdraw a pending one
//
// All need `hq:organizations` and a second factor; the server checks both on every request.
//
// **The owner.** The api says whether an organisation's owner has joined and what became of its newest
// owner invitation (ADR-0046), so the page reads it rather than guessing. What the api hands over once — a
// new invitation's link, when its message could not go — is kept for this session under `lastInvitation`,
// which sign-out clears with the rest.

import type {
  ApiClient,
  CreatedInvitation,
  HqOrganization,
  HqOrganizationList,
  HqOrganizationOwner,
  HqOrganizationPayments,
  OrganizationDecision,
  Payee,
  OwnerInvitationContact,
  SavePayeeRequest,
} from '@clavian/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';
import { queryKeys } from '../../api/queries';
import { readSettings, writeSettings } from '../../settings/registry-source';

/** A page of the list: the api's maximum, so a pilot's organisations are one page. */
const PAGE_SIZE = 100;

/** Which organisations the list shows: every one, or those whose owner has not joined yet. */
export type OrganizationListFilter = 'all' | 'not_joined';

export const organizationKeys = {
  all: ['hq', 'organizations'] as const,
  list: ['hq', 'organizations', 'list'] as const,
  /** The list narrowed to the cafés whose owner has not joined yet. */
  listNotJoined: ['hq', 'organizations', 'list', 'not_joined'] as const,
  one: (organizationId: string) => ['hq', 'organizations', 'one', organizationId] as const,
  payments: (organizationId: string) => ['hq', 'organizations', 'payments', organizationId] as const,
  owner: (organizationId: string) => ['hq', 'organizations', 'owner', organizationId] as const,
  /** The invitation this session made last, with the link the api hands over once. */
  lastInvitation: (organizationId: string) => ['hq', 'organizations', 'last-invitation', organizationId] as const,
};

/**
 * The params of an organisation's page, as HQ's links name them: plain strings, because the router's
 * registered route tree is the organisation console's (`src/hq/paths.ts`).
 */
export const organizationParams = (organizationId: string): Readonly<Record<string, string>> => ({ organizationId });

export function listOrganizationsPage(api: ApiClient, cursor: string | undefined, filter: OrganizationListFilter = 'all'): Promise<HqOrganizationList> {
  return api.listHqOrganizations({
    query: { limit: PAGE_SIZE, ...(cursor === undefined ? {} : { cursor }), ...(filter === 'not_joined' ? { owner: 'not_joined' as const } : {}) },
  });
}

/** Every organisation — or those whose owner has not joined yet — a page at a time. */
export function useOrganizationList(filter: OrganizationListFilter = 'all') {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: filter === 'all' ? organizationKeys.list : organizationKeys.listNotJoined,
    queryFn: ({ pageParam }) => listOrganizationsPage(api, pageParam, filter),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

export function useOrganization(organizationId: string) {
  const api = useApi();
  return useQuery<HqOrganization>({
    queryKey: organizationKeys.one(organizationId),
    queryFn: () => api.getHqOrganization({ path: { organizationId } }),
  });
}

/**
 * Approve or decline a `pending` organisation (ADR-0019 §6, F-HQ-02): `approve` moves it to `active`,
 * `decline` (with a reason) to `suspended`. Needs `hq:organizations` and a second factor; the server
 * checks both. On success the organisation and the list are re-read so the new status shows.
 */
export function useDecideOrganization(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OrganizationDecision) => api.decideHqOrganization({ path: { organizationId }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationKeys.one(organizationId) });
      void queryClient.invalidateQueries({ queryKey: organizationKeys.list });
    },
  });
}

/** Whether the organisation's owner has joined, and its newest owner invitation (ADR-0046). */
export function useOrganizationOwner(organizationId: string) {
  const api = useApi();
  return useQuery<HqOrganizationOwner>({
    queryKey: organizationKeys.owner(organizationId),
    queryFn: () => api.getHqOrganizationOwner({ path: { organizationId } }),
  });
}

/** The invitation this session made last for the organisation, with its link: read from the cache, never from the api. */
export function useLastInvitation(organizationId: string) {
  return useQuery<CreatedInvitation | null>({
    queryKey: organizationKeys.lastInvitation(organizationId),
    queryFn: () => null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** An invitation just made: kept for the page to show once, and the owner and the lists read again. */
async function invitationMade(queryClient: QueryClient, organizationId: string, invitation: CreatedInvitation): Promise<void> {
  queryClient.setQueryData<CreatedInvitation | null>(organizationKeys.lastInvitation(organizationId), invitation);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: organizationKeys.owner(organizationId) }),
    queryClient.invalidateQueries({ queryKey: organizationKeys.list }),
  ]);
}

/** Invite an owner — someone new — while the organisation has none. */
export function useInviteOwner(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OwnerInvitationContact) => api.createHqOwnerInvitation({ path: { organizationId }, body }),
    onSuccess: (invitation) => invitationMade(queryClient, organizationId, invitation),
  });
}

/** A fresh invitation to the same email or phone; every pending one withdrawn (ADR-0046). */
export function useResendOwnerInvitation(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.resendHqOwnerInvitation({ path: { organizationId, invitationId } }),
    onSuccess: (invitation) => invitationMade(queryClient, organizationId, invitation),
  });
}

/** Withdraw a pending owner invitation: its link stops working at once. */
export function useWithdrawOwnerInvitation(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.withdrawHqOwnerInvitation({ path: { organizationId, invitationId } }),
    onSuccess: async () => {
      queryClient.setQueryData<CreatedInvitation | null>(organizationKeys.lastInvitation(organizationId), null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationKeys.owner(organizationId) }),
        queryClient.invalidateQueries({ queryKey: organizationKeys.list }),
      ]);
    },
  });
}

/** A pending owner invitation's link, to pass on. Reading it is recorded in the organisation's log. */
export function useOwnerInvitationLink(organizationId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (invitationId: string) => api.getHqOwnerInvitationLink({ path: { organizationId, invitationId } }),
  });
}

/** A new organisation: on its page from the start, its invitation's outcome shown once, and at the top of the list. */
export async function rememberCreated(queryClient: QueryClient, organization: HqOrganization, invitation: CreatedInvitation): Promise<void> {
  queryClient.setQueryData(organizationKeys.one(organization.id), organization);
  queryClient.setQueryData<CreatedInvitation | null>(organizationKeys.lastInvitation(organization.id), invitation);
  await queryClient.invalidateQueries({ queryKey: organizationKeys.list });
}

/** An organisation's online-payments setup as head office sees it: provider, commission and payee (masked). */
export function useOrganizationPayments(organizationId: string, enabled = true) {
  const api = useApi();
  return useQuery<HqOrganizationPayments>({
    queryKey: organizationKeys.payments(organizationId),
    queryFn: () => api.getHqOrganizationPayments({ path: { organizationId } }),
    enabled,
  });
}

/** Onboard or correct the payee. The full account number and PAN go to the provider and are never kept. */
export function useSavePayee(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SavePayeeRequest) => api.saveHqOrganizationPayee({ path: { organizationId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.payments(organizationId) }),
  });
}

/** Ask the provider where it is with the payee now. */
export function useRefreshPayee(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Payee>({
    mutationFn: () => api.refreshHqOrganizationPayee({ path: { organizationId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.payments(organizationId) }),
  });
}

/**
 * Set the organisation's commission, in basis points, through its settings (`payments.commissionBps`, `hq:settings`).
 * The settings are read first for their current version, so a write never overwrites a newer one.
 */
export function useSaveCommission(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bps: number) => {
      const scope = { kind: 'hqOrganization', organizationId } as const;
      const current = await readSettings(api, scope);
      return writeSettings(api, scope, [{ key: 'payments.commissionBps', value: bps }], current.etag);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationKeys.payments(organizationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.hqOrganizationSettings(organizationId) }),
      ]);
    },
  });
}
